import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "@store/baseQuery";

export const mockupApi = createApi({
    reducerPath: "mockupApi",
    baseQuery: customBaseQuery,
    tagTypes: ["MockupLab"],
    endpoints: (builder) => ({
        getMockupLab: builder.query<any, void>({
            query: () => ({
                url: "/mockuplab/projects/",
                method: "GET",
            }),
            providesTags: ["MockupLab"],
        }),

        createProject: builder.mutation({
            query: (body) => ({
                url: `/mockuplab/projects/`,
                method: "POST",
                body,
            }),
        }),

        getProjectDetails: builder.query({
            query: (id) => ({
                url: `/mockuplab/projects/${id}/`,
                method: "GET",
            }),
        }),

        patchProjectDetails: builder.mutation({
            query: ({ id, body }) => ({
                url: `/mockuplab/projects/${id}/`,
                method: "PATCH",
                body,
            }),
            invalidatesTags: ["MockupLab"],
        }),

        getTemplates: builder.query({
            query: () => ({
                url: `/mockuplab/templates/`,
                method: "GET",
            }),
        }),

        generateDashboard: builder.mutation({
            query: (id) => ({
                url: `/mockuplab/projects/${id}/generate/`,
                method: "POST",
            }),
        }),

        generateTemplate: builder.query<string, string>({
            async queryFn(id, _queryApi, _extraOptions, baseQuery) {
                const result: any = await baseQuery({
                    url: `/mockuplab/public/${id}/`,
                    method: "GET",
                });

                if (result.error) {
                    if (
                        result.error.status === "PARSING_ERROR" &&
                        result.error.originalStatus === 200 &&
                        result.error.data
                    ) {
                        return { data: result.error.data };
                    }
                    return { error: result.error };
                }

                if (typeof result.data === "string") {
                    return { data: result.data };
                }

                if (result.data?.data) {
                    return { data: result.data.data };
                }

                return { error: { status: "CUSTOM_PARSE_ERROR", message: "Unexpected response" } };
            },
        }),

        uploadProjectAsset: builder.mutation<
            any,
            {
                projectId: string;
                assetType: 'logo';
                file: FormData;
            }
        >({
            query: ({ projectId, assetType, file }) => ({
                url: `/mockuplab/projects/${projectId}/upload_asset/?type=${assetType}`,
                method: "POST",
                body: file,
            }),
            invalidatesTags: ["MockupLab"],
        }),

        removeProjectAsset: builder.mutation<
            any,
            {
                projectId: string;
                assetType: 'logo';
            }
        >({
            query: ({ projectId, assetType }) => {
                let body: any = {};

                if (assetType === 'logo') {
                    body = {
                        config: {
                            branding: {
                                logo_url: null
                            }
                        }
                    };
                }

                return {
                    url: `/mockuplab/projects/${projectId}/`,
                    method: "PATCH",
                    body,
                };
            },
            invalidatesTags: ["MockupLab"],
        }),
    }),
});

export const {
    useLazyGetMockupLabQuery,
    useCreateProjectMutation,
    useLazyGetProjectDetailsQuery,
    usePatchProjectDetailsMutation,
    useLazyGetTemplatesQuery,
    useGenerateDashboardMutation,
    useUploadProjectAssetMutation,
    useRemoveProjectAssetMutation
} = mockupApi;
